/** Chrome component sx (AppBar, Drawer, hamburger, drawers). Same px. */

import {
  CHROME_TRANSITION,
  DARK_MODE_SEL,
  HEADER_HEIGHT,
  HEADER_SHADOW,
  SIDEBAR_DARK_BG,
  SIDEBAR_SHADOW,
  SIDEBAR_WIDTH,
  SIDEBAR_WIDTH_COLLAPSED,
  chromeHeaderDarkSx,
  chromeSidebarDarkSx,
} from './chromeSx'

const TRANSITION = 'all 0.2s ease-in-out'
const DRAWER_TRANSITION = CHROME_TRANSITION
const SPACER = '1rem'
const LAYOUT = '3rem'
const WHITE = '#fff'
const BLACK = '#000'
const PRIMARY = '#368cff'
const RADIUS = '0.65rem'
const RADIUS_SM = '0.29rem'
const RADIUS_LG = '0.75rem'
const FOOTER_HEIGHT = 70
const SIDEBAR_FOOTER_HEIGHT = 54
const NAVLINK_H = 44
const NAVLINK_INNER_H = 36
const BOX_SHADOW_SM =
  '0 0.313rem 0.8rem rgba(79, 80, 98, 0.5), 0 0.126rem 0.225rem rgba(79, 80, 98, 0.3)'
const BOX_SHADOW_SM_LIGHT = HEADER_SHADOW
const BOX_SHADOW_LAYOUT_TOP =
  '0 -0.46875rem 2.1875rem rgba(59, 62, 102, 0.03), 0 -0.9375rem 1.40625rem rgba(59, 62, 102, 0.03), 0 -0.25rem 0.53125rem rgba(59, 62, 102, 0.05), 0 -0.125rem 0.1875rem rgba(59, 62, 102, 0.03)'
const BOX_SHADOW_LAYOUT_LEFT =
  '-0.46875rem 0 2.1875rem rgba(59, 62, 102, 0.03), -0.9375rem 0 1.40625rem rgba(59, 62, 102, 0.03), -0.25rem 0 0.53125rem rgba(59, 62, 102, 0.05), -0.125rem 0 0.1875rem rgba(59, 62, 102, 0.03)'
const SIDEBAR_SHADOW_LIGHT = '0px 0px 28px 0px rgba(54, 140, 255, 0.08)'
const UP_ML = '@media (min-width: 950px)'
const DOWN_ML = '@media (max-width: 949.98px)'
const UP_LG = '@media (min-width: 1100px)'
const DOWN_LG = '@media (max-width: 1099.98px)'
const DOWN_MD = '@media (max-width: 767.98px)'
const DOWN_SM = '@media (max-width: 575.98px)'

const hamburgerInner = {
  width: 24,
  height: 2,
  backgroundColor: BLACK,
  borderRadius: 10,
  position: 'absolute',
  transitionProperty: 'transform',
  transitionDuration: '0.15s',
  transitionTimingFunction: 'ease',
}

export const headerPaneSx = {
  display: 'flex',
  alignItems: 'center',
  minWidth: 0,
}

export const headerPaneEndSx = {
  ...headerPaneSx,
  flex: '1 1 auto',
  justifyContent: 'flex-end',
  gap: '0.5rem',
  overflow: 'hidden',
  '& .MuiIconButton-root, & .MuiButton-root': {
    flexShrink: 0,
  },
}

export function hamburgerButtonSx({
  active = false,
  showBelowMl = false,
  mobileOnly = false,
} = {}) {
  return {
    padding: 0,
    display: mobileOnly || showBelowMl ? 'none' : 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    transitionProperty: 'opacity, filter',
    transitionDuration: '0.15s',
    transitionTimingFunction: 'linear',
    font: 'inherit',
    color: 'inherit',
    textTransform: 'none',
    backgroundColor: 'transparent',
    border: 0,
    margin: 0,
    overflow: 'visible',
    '&:hover': { opacity: 0.7 },
    '&:focus, &:active': { outline: 'none' },
    ...(showBelowMl
      ? {
          [DOWN_ML]: { display: 'flex' },
        }
      : {}),
    ...(mobileOnly
      ? {
          [DOWN_MD]: { display: 'flex' },
        }
      : {}),
    '& [data-hf="hamburger-inner"]': {
      display: 'block',
      top: 1,
      marginTop: -1,
      ...hamburgerInner,
      backgroundColor: `${PRIMARY} !important`,
      transitionDuration: '0.275s',
      transitionTimingFunction: 'cubic-bezier(0.68, -0.55, 0.265, 1.55)',
      '&::before, &::after': {
        ...hamburgerInner,
        backgroundColor: `${PRIMARY} !important`,
        content: '""',
        display: 'block',
      },
      '&::before': {
        top: 6,
        transition: 'opacity 0.125s 0.275s ease',
      },
      '&::after': {
        top: 12,
        transition: 'transform 0.275s cubic-bezier(0.68, -0.55, 0.265, 1.55)',
      },
      ...(active
        ? {
            transform: 'translate3d(0, 6px, 0) rotate(135deg)',
            transitionDelay: '0.075s',
            '&::before': { transitionDelay: '0s', opacity: 0 },
            '&::after': {
              transform: 'translate3d(0, -12px, 0) rotate(-270deg)',
              transitionDelay: '0.075s',
            },
          }
        : {}),
    },
  }
}

export const hamburgerBoxSx = {
  width: 24,
  height: 14,
  display: 'inline-block',
  position: 'relative',
}

export function sidebarPaperSx({
  collapsed = false,
  mobileOpen = false,
  shadow = false,
  isDark = true,
  isMini = false,
  fixed = true,
} = {}) {
  return {
    width: SIDEBAR_WIDTH,
    flexDirection: 'column',
    display: 'flex',
    height: '100%',
    transition: CHROME_TRANSITION,
    zIndex: 1115,
    position: 'relative',
    boxSizing: 'border-box',
    border: 'none',
    backgroundImage: 'none',
    backgroundColor: `${isDark ? SIDEBAR_DARK_BG : WHITE} !important`,
    boxShadow: shadow ? SIDEBAR_SHADOW : 'none',
    '& [data-hf="sidebar-content"]': {
      overflowY: 'auto',
      overflowX: 'visible',
      height: '100%',
      backgroundColor: 'inherit',
      backgroundImage: 'none',
    },
    ...(collapsed
      ? {
          [UP_ML]: {
            width: SIDEBAR_WIDTH_COLLAPSED,
            position: 'fixed',
            left: 0,
            display: 'flex',
            '& .sidebar-show-on-hover': { display: 'none' },
            '&:hover .sidebar-show-on-hover': { display: 'block' },
            ...(!isMini
              ? {
                  '&:hover': {
                    width: SIDEBAR_WIDTH,
                    position: 'fixed',
                    boxShadow: SIDEBAR_SHADOW,
                  },
                }
              : {}),
            ...collapsedDescendants({ isDark, isMini  })[UP_ML],
          },
        }
      : {}),
    ...(isDark ? sidebarDarkDescendants : sidebarLightDescendants),
    [DARK_MODE_SEL]: chromeSidebarDarkSx,
  }
}

const sidebarDarkDescendants = {
  '& [data-hf="hamburger-inner"], & [data-hf="hamburger-inner"]::after, & [data-hf="hamburger-inner"]::before':
    {
      backgroundColor: `${WHITE} !important`,
    },
  '& [data-hf="sidebar-header"]': {
    backgroundColor: SIDEBAR_DARK_BG,
    backgroundImage: 'none',
    '& [data-hf="collapse-sidebar-btn"]': {
      background: 'transparent',
      color: 'rgba(255, 255, 255, 0.8)',
      opacity: 1,
      '&:hover': {
        background: 'rgba(0, 0, 0, 0.2)',
        color: WHITE,
      },
    },
    '& [data-hf="expand-sidebar-btn"]': {
      border: `${WHITE} solid 2px`,
      background: WHITE,
      color: PRIMARY,
      boxShadow: BOX_SHADOW_SM,
    },
    '&::after': { background: 'rgba(255, 255, 255, 0.15)' },
  },
  'html[data-color-mode="dark"] & [data-hf="sidebar-header"]':
    chromeSidebarDarkSx,
  '& [data-hf="sidebar-logo-text"] span': {
    color: WHITE,
    opacity: 0.7,
  },
  '& [data-hf="sidebar-logo-text"] b': {
    color: WHITE,
  },
  '& [data-hf="sidebar-nav"] [data-hf="sidebar-menu-box"]': {
    background: 'rgba(0, 0, 0, 0.15)',
  },
  '& [data-hf="sidebar-nav"] [data-hf="sidebar-header-label"]': {
    color: 'rgba(255, 255, 255, 0.35)',
  },
  '& [data-hf="sidebar-nav"] ul li a': {
    color: 'rgba(255, 255, 255, 0.85) !important',
    '& .MuiTypography-root': { color: 'inherit !important' },
    '& [data-hf="sidebar-icon"] > *': { color: 'currentColor', opacity: 1 },
  },
  '& [data-hf="sidebar-nav"] ul li a:hover, & [data-hf="sidebar-nav"] ul li a.active, & [data-hf="sidebar-nav"] ul li a[aria-current="page"], & [data-hf="sidebar-nav"] ul li a[data-nav-active]': {
    color: 'rgba(255, 255, 255, 1) !important',
    background: 'rgba(255, 255, 255, 0.14)',
    '& .MuiTypography-root': { color: 'inherit !important' },
    '& [data-hf="sidebar-icon-indicator"]': { opacity: 0.7 },
    '& [data-hf="sidebar-icon"] > *': { opacity: 1 },
    '& [data-hf="sidebar-actions-btn"]': {
      color: '#1a1a1a !important',
      backgroundColor: 'rgba(255, 255, 255, 0.92) !important',
      '&:hover, &:focus, &.Mui-focusVisible, &:active': {
        backgroundColor: '#fff !important',
        color: '#1a1a1a !important',
      },
      '& svg, & .MuiSvgIcon-root': { color: '#1a1a1a !important' },
    },
  },
  '& [data-hf="sidebar-userbox"]': {
    color: WHITE,
    background: 'rgba(255, 255, 255, 0.03)',
    '& [data-hf="btn-userbox"]': {
      background: 'rgba(255, 255, 255, 0.1)',
      color: 'rgba(255, 255, 255, 0.8)',
      '&:hover': {
        background: 'rgba(255, 255, 255, 0.2)',
        color: 'rgba(255, 255, 255, 1)',
      },
    },
    '& .avatar-icon': { boxShadow: '0 0 0 3px rgba(255, 255, 255, 0.5)' },
    '& .badge-circle': { boxShadow: '0 0 0 2px rgba(255, 255, 255, 0.5)' },
  },
  '& [data-hf="sidebar-footer"]': {
    background: 'rgba(255, 255, 255, 0.06)',
    boxShadow:
      '0 -0.36875rem 0.1675rem rgba(0, 0, 0, 0.04), 0 -0.8375rem 0.30625rem rgba(0, 0, 0, 0.04), 0 -0.2rem 0.11125rem rgba(0, 0, 0, 0.06), 0 -0.1rem 0.0775rem rgba(0, 0, 0, 0.04)',
    '& .MuiButton-root': {
      color: 'rgba(255, 255, 255, 0.5)',
      '&.active, &:active, &:focus, &:hover': {
        color: 'rgba(255, 255, 255, 0.9)',
        background: 'rgba(255, 255, 255, 0.09)',
      },
    },
    '& ul li': { borderRightColor: 'rgba(255, 255, 255, 0.08)' },
  },
  '& [data-hf="sidebar-menu-collapsed"] li a': {
    background: 'transparent',
    color: 'rgba(255, 255, 255, 0.6)',
    '&:hover': {
      background: 'rgba(255, 255, 255, 0.1)',
      color: WHITE,
    },
    '&.active, &[aria-current="page"], &[data-nav-active]': { background: WHITE, color: PRIMARY },
  },
}

const sidebarLightDescendants = {
  '& [data-hf="hamburger-inner"], & [data-hf="hamburger-inner"]::after, & [data-hf="hamburger-inner"]::before':
    {
      backgroundColor: `${PRIMARY} !important`,
    },
  '& [data-hf="sidebar-header"]': {
    backgroundColor: WHITE,
    backgroundImage: 'none',
    '& [data-hf="collapse-sidebar-btn"]': {
      background: 'transparent',
      color: 'rgba(54, 140, 255, 0.8)',
      opacity: 1,
      '&:hover': {
        background: 'rgba(54, 140, 255, 0.2)',
        color: PRIMARY,
      },
    },
    '& [data-hf="expand-sidebar-btn"]': {
      border: `${PRIMARY} solid 2px`,
      background: PRIMARY,
      color: WHITE,
      boxShadow: BOX_SHADOW_SM,
    },
    '&::after': { background: 'rgba(0, 0, 0, 0.08)' },
  },
  '& [data-hf="sidebar-logo-icon"]': {
    background: PRIMARY,
  },
  '& [data-hf="sidebar-logo-text"] span': {
    color: PRIMARY,
    opacity: 0.7,
  },
  '& [data-hf="sidebar-logo-text"] b': {
    color: BLACK,
  },
  '& [data-hf="sidebar-nav"] [data-hf="sidebar-menu-box"]': {
    background: 'rgba(54, 140, 255, 0.03)',
  },
  '& [data-hf="sidebar-nav"] [data-hf="sidebar-header-label"]': {
    color: 'rgba(0, 0, 0, 0.85)',
  },
  '& [data-hf="sidebar-nav"] ul li a': {
    color: 'rgba(0, 0, 0, 0.8) !important',
    '& .MuiTypography-root': { color: 'inherit !important' },
    '& [data-hf="sidebar-icon"] > *': { color: 'currentColor', opacity: 1 },
    '& [data-hf="sidebar-icon-indicator"]': { opacity: 0.4 },
  },
  '& [data-hf="sidebar-nav"] ul li a:hover, & [data-hf="sidebar-nav"] ul li a.active, & [data-hf="sidebar-nav"] ul li a[aria-current="page"], & [data-hf="sidebar-nav"] ul li a[data-nav-active]': {
    color: PRIMARY,
    background: 'rgba(54, 140, 255, 0.05)',
    '& .MuiTypography-root': { color: 'inherit !important' },
    '& [data-hf="sidebar-icon-indicator"]': { opacity: 0.7 },
    '& [data-hf="sidebar-icon"] > *': { opacity: 1, color: PRIMARY },
  },
  '& [data-hf="sidebar-userbox"]': {
    color: BLACK,
    background: 'rgba(54, 140, 255, 0.025)',
    '& .text-white-50': { color: 'rgba(0, 0, 0, 0.5) !important' },
    '& [data-hf="btn-userbox"]': {
      background: 'rgba(54, 140, 255, 0.1)',
      color: 'rgba(54, 140, 255, 0.8)',
      '&:hover': {
        background: 'rgba(54, 140, 255, 0.2)',
        color: 'rgba(54, 140, 255, 1)',
      },
    },
    '& .avatar-icon': { boxShadow: '0 0 0 3px rgba(255, 255, 255, 0.8)' },
    '& .badge-circle': { boxShadow: '0 0 0 2px rgba(255, 255, 255, 0.8)' },
  },
  '& [data-hf="sidebar-footer"]': {
    background: 'rgba(54, 140, 255, 0.025)',
    boxShadow:
      '0 -0.46875rem 0.1375rem rgba(209, 210, 219, 0.08), 0 -0.9375rem 0.32625rem rgba(209, 210, 219, 0.08), 0 -0.25rem 0.12125rem rgba(209, 210, 219, 0.15), 0 -0.125rem 0.0775rem rgba(209, 210, 219, 0.08)',
    '& .MuiButton-root': {
      color: 'rgba(54, 140, 255, 0.7)',
      '& svg': { fill: 'rgba(54, 140, 255, 0.7)' },
      '&.active, &:active, &:focus, &:hover': {
        color: PRIMARY,
        background: 'rgba(54, 140, 255, 0.1)',
        '& svg': { fill: PRIMARY },
      },
    },
    '& ul li': { borderRightColor: 'rgba(54, 140, 255, 0.1)' },
  },
  '& [data-hf="sidebar-menu-collapsed"] li a': {
    background: 'transparent',
    color: 'rgba(54, 140, 255, 0.5)',
    '&.active, &[aria-current="page"], &[data-nav-active], &:hover': {
      background: 'rgba(54, 140, 255, 0.1)',
      color: PRIMARY,
    },
  },
}

function collapsedDescendants({ isDark, isMini  }) {
  return {
    [UP_ML]: {
      ...(isDark
        ? {
            '&:not(:hover) [data-hf="sidebar-header-label"]::after': {
              background: 'rgba(255, 255, 255, 0.15)',
            },
          }
        : {
            '&:not(:hover) [data-hf="sidebar-header-label"]::after': {
              background: 'rgba(0, 0, 0, 0.15)',
            },
          }),
      '& [data-hf="collapse-sidebar-btn"]': {
        visibility: 'hidden',
        opacity: 0,
        display: 'none',
      },
      '& [data-hf="sidebar-header"] [data-hf="sidebar-logo"]': {
        margin: '0 auto',
        '& [data-hf="sidebar-logo-text"]': {
          opacity: 0,
          height: 0,
          width: 0,
          overflow: 'hidden',
          visibility: 'hidden',
          padding: 0,
        },
      },
      ...(!isMini
        ? {
            '&:hover [data-hf="sidebar-header"] [data-hf="sidebar-logo"]': {
              margin: 0,
              '& [data-hf="sidebar-logo-text"]': {
                opacity: 1,
                height: 'auto',
                width: 'auto',
                overflow: 'visible',
                visibility: 'visible',
                padding: '0 0 0 0.6667rem',
              },
            },
            '&:hover [data-hf="sidebar-header"] [data-hf="expand-sidebar-btn"]': {
              visibility: 'visible',
              opacity: 1,
            },
            '&:hover [data-hf="sidebar-userbox"]': {
              '& [data-hf="userbox-details"], & [data-hf="btn-userbox"], & [data-hf="card-tr-actions"]': {
                display: 'block',
              },
              '& [data-hf="sidebar-limits-collapsed"]': { display: 'none !important' },
              '& [data-hf="card-tr-actions"]': { visibility: 'visible', opacity: 1 },
              background: isDark
                ? 'rgba(255, 255, 255, 0.03)'
                : 'rgba(54, 140, 255, 0.03)',
            },
            '&:hover [data-hf="sidebar-footer"]': {
              opacity: 1,
              visibility: 'visible',
              height: SIDEBAR_FOOTER_HEIGHT,
            },
          }
        : {}),
      '&:not(:hover) [data-hf="sidebar-nav"] [data-hf="sidebar-menu-box"]': {
        padding: '0.5rem',
        '& [data-hf="sidebar-header-label"]': { display: 'none' },
      },
      '&:not(:hover) [data-hf="sidebar-nav"] [data-hf="sidebar-header-label"]': {
        paddingTop: 0,
        paddingBottom: 0,
        height: 0,
        margin: '1.5rem',
        '&::after': {
          visibility: 'visible',
          opacity: 1,
          height: '1px',
          content: '""',
          position: 'absolute',
          width: '80%',
          left: '10%',
        },
        '& span': {
          visibility: 'hidden',
          opacity: 0,
          height: 0,
          overflow: 'hidden',
        },
      },
      '&:not(:hover) [data-hf="sidebar-nav"] ul li a': {
        fontSize: 0,
        '& [data-hf="sidebar-icon"]': { margin: '0 auto' },
        '& [data-hf="sidebar-icon-indicator"]': {
          opacity: 0,
          visibility: 'hidden',
          height: 0,
          width: 0,
          overflow: 'hidden',
          display: 'none',
        },
        '& [data-hf="sidebar-item-label"]': {
          opacity: 0,
          height: 0,
          overflow: 'hidden',
          width: 0,
          visibility: 'hidden',
          '& .badge': { opacity: 0, visibility: 'hidden' },
        },
      },
      '&:not(:hover) [data-hf="sidebar-nav"] ul li ul': {
        height: 0,
        padding: 0,
        overflow: 'hidden',
      },
      '&:not(:hover) [data-hf="sidebar-limits-divider"]': { display: 'none' },
      '&:not(:hover) [data-hf="sidebar-userbox"]': {
        margin: '0.35rem 0.4rem 0.6rem',
        padding: '0.4rem 0.3rem',
        '& [data-hf="sidebar-limits-collapsed"]': { display: 'flex !important' },
      },
      '&:not(:hover) [data-hf="upgrade-to-hot-nav-item"]': {
        minHeight: 'auto !important',
        padding: '0.75rem !important',
        marginLeft: '0.5rem !important',
        marginRight: '0.5rem !important',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'column',
        '& [data-hf="upgrade-box-content"]': { display: 'none !important' },
        '& [data-hf="upgrade-box-description"]': { display: 'none !important' },
        '& [data-hf="upgrade-box-collapsed"]': { display: 'flex !important' },
      },
      '&:hover [data-hf="upgrade-to-hot-nav-item"]': {
        padding: '1rem !important',
        marginLeft: '0.75rem !important',
        marginRight: '0.75rem !important',
        flexDirection: 'column',
        alignItems: 'stretch',
        '& [data-hf="upgrade-box-content"]': { display: 'flex !important' },
        '& [data-hf="upgrade-box-description"]': { display: 'block !important' },
        '& [data-hf="upgrade-box-collapsed"]': { display: 'none !important' },
      },
      '& [data-hf="sidebar-userbox"]': {
        background: 'transparent',
        '& [data-hf="userbox-details"], & [data-hf="btn-userbox"]': { display: 'none' },
        '& [data-hf="card-tr-actions"]': { visibility: 'hidden', opacity: 0 },
      },
      '& [data-hf="sidebar-footer"]': {
        opacity: 0,
        visibility: 'hidden',
        height: 0,
      },
    },
  }
}

export const sidebarContentSx = {
  overflowY: 'auto',
  overflowX: 'visible',
  height: '100%',
  backgroundColor: 'inherit',
  backgroundImage: 'none',
}

export const sidebarHeaderSx = {
  padding: `0 1.5rem`,
  minHeight: HEADER_HEIGHT,
  height: HEADER_HEIGHT,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  position: 'relative',
  zIndex: 1,
  flexShrink: 0,
  marginBottom: '0.75rem',
  backgroundColor: SIDEBAR_DARK_BG,
  backgroundImage: 'none',
  [DARK_MODE_SEL]: chromeSidebarDarkSx,
  '&::after': {
    content: '""',
    width: '100%',
    left: 0,
    right: 0,
    top: 'auto',
    bottom: 0,
    height: '1px',
    position: 'absolute',
  },
  [DOWN_MD]: {
    '& [data-hf="toggle-mobile-sidebar-btn"]': { display: 'block' },
    '& [data-hf="collapse-sidebar-btn"], & [data-hf="expand-sidebar-btn"]': {
      display: 'none',
    },
  },
}

export const sidebarLogoSx = {
  display: 'flex',
  alignItems: 'center',
  transition: CHROME_TRANSITION,
  '&:hover': { color: BLACK },
}

export const sidebarLogoIconSx = {
  borderRadius: RADIUS_SM,
  width: 40,
  height: 40,
  background: WHITE,
  display: 'flex',
  alignItems: 'center',
  alignContent: 'center',
  boxShadow: '1px 1px 3px rgba(0, 0, 0, 0.2)',
  '& img': { width: 26, margin: '0 auto' },
}

export const sidebarLogoTextSx = {
  lineHeight: 1,
  padding: '0 0 0 0.6667rem',
  transition: CHROME_TRANSITION,
  '& span': {
    display: 'block',
    textTransform: 'uppercase',
    fontSize: '0.729rem',
    color: WHITE,
    opacity: 0.7,
    padding: '4px 0',
  },
  '& b': {
    fontSize: '1.265rem',
    fontWeight: 'bold',
    color: WHITE,
    textTransform: 'lowercase',
  },
}

export const collapseSidebarBtnSx = {
  visibility: 'visible',
  transition: TRANSITION,
  padding: 0,
  width: 36,
  height: 36,
  lineHeight: '34px',
  flexShrink: 0,
  minWidth: 36,
}

export const expandSidebarBtnSx = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  transition: TRANSITION,
  position: 'absolute',
  right: 0,
  width: 36,
  height: 36,
  lineHeight: '34px',
  margin: '-18px -18px 0 0',
  top: '50%',
  padding: 0,
  visibility: 'hidden',
  opacity: 0,
  flexShrink: 0,
  minWidth: 36,
}

export const sidebarFooterSx = {
  height: SIDEBAR_FOOTER_HEIGHT,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  padding: 0,
  transition: TRANSITION,
  margin: 0,
  borderRadius: 0,
  '& .MuiButton-root': {
    boxShadow: 'none !important',
    fontSize: '1rem',
  },
  '& ul': {
    paddingLeft: 0,
    listStyle: 'none',
    margin: 0,
    display: 'flex',
    justifyContent: 'center',
  },
  '& ul li': {
    borderRight: 'transparent solid 1px',
    padding: '0.25rem 0',
    '&:last-child': { borderRight: 0 },
  },
}

/** Sidebar nav link active state (replaces Bootstrap `.active` class). */
export function sidebarNavActive(isSelected, onPage) {
  const active = Boolean(isSelected && onPage)
  return {
    'data-nav-active': active || undefined,
    'aria-current': active ? 'page' : undefined,
  }
}

export const loadingBackdropSx = {
  color: '#fff',
  zIndex: 2000,
}

export const headerTrialUpgradeBtnSx = {
  mr: '20px',
  borderRadius: '10px',
  backgroundColor: '#368CFF',
  color: 'white',
}

export const sidebarRowGridSx = {
  width: '100%',
  minWidth: 0,
  justifyContent: 'space-between',
  alignItems: 'center',
}

export const sidebarRowNameSx = {
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}

export const sidebarRowCountColSx = {
  minWidth: 24,
  width: 'auto',
  flexShrink: 0,
  textAlign: 'right',
}

export const sidebarRowActionsColSx = {
  flexShrink: 0,
  mr: '-8px',
}

export const sidebarActionsBtnSx = {
  backgroundColor: 'rgba(255, 255, 255, 0.92) !important',
  color: '#1a1a1a !important',
  width: 30,
  height: 30,
  '& svg, & .MuiSvgIcon-root': { color: '#1a1a1a !important' },
  '&:hover, &:focus, &:focus-visible, &.Mui-focusVisible, &:active': {
    backgroundColor: '#fff !important',
    color: '#1a1a1a !important',
  },
  '&:hover svg, &:focus svg, &:active svg, &:hover .MuiSvgIcon-root, &:focus .MuiSvgIcon-root':
    {
      color: '#1a1a1a !important',
    },
}

export const sidebarNestedLinkIconSx = {
  mr: '10px',
}

export const sidebarNavItemCountSx = {
  position: 'absolute',
  right: 24,
  top: '50%',
  marginTop: '-11px',
  height: 22,
  lineHeight: '22px',
  width: 8,
}

export const sidebarNavActionsWrapSx = {
  position: 'absolute',
  right: 8,
  top: '50%',
  marginTop: '-15px',
}

export const sidebarLogoLinkSx = {
  display: 'flex',
  alignItems: 'center',
  textDecoration: 'none',
}

export const sidebarLogoImgSx = {
  maxHeight: '50px',
  maxWidth: '100%',
}

export const sidebarLogoSmallImgSx = {
  maxHeight: '75px',
  maxWidth: '100%',
}

export const poweredByLogoSx = {
  width: 150,
  mx: '5px',
}

export const footerLegalLinkSx = {
  color: '#a7aabb',
  borderRadius: '0.29rem',
}

export const footerLegalLinkSpacedSx = {
  color: '#a7aabb',
  borderRadius: '0.29rem',
  mx: '20px',
}

export const footerSocialListSx = {
  pr: '75px',
}

export const footerSocialIconSx = {
  color: '#cfcfcf',
}

export const headerUserMenuGridSx = {
  m: '10px',
  justifyContent: 'center',
  alignItems: 'center',
}

export const headerUserMenuNameSx = {
  fontWeight: 600,
}

export const headerUserMenuEmailSx = {
  mb: '10px',
  p: '10px',
}

export const headerUserUploadBtnSx = {
  width: 40,
  height: 40,
  minWidth: 40,
  p: 0,
  overflow: 'hidden',
  color: '#fff',
  backgroundColor: '#4191ff',
  boxShadow:
    '0 0.125rem 0.25rem rgba(7, 9, 25, 0.075), 0 0 0 2px #fff',
  '& .MuiSvgIcon-root': {
    color: '#fff',
    width: 20,
    height: 20,
  },
  'html[data-color-mode="dark"] &': {
    boxShadow:
      '0 0.125rem 0.25rem rgba(0, 0, 0, 0.45), 0 0 0 2px #1e1e1e',
  },
}

export const headerUserPlaceholderImgSx = {
  height: 140,
  width: 140,
  lineHeight: '140px',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  p: 0,
}

export const headerUserActionPrimarySx = {
  bgcolor: '#358cff',
  width: 50,
  minWidth: 50,
}

export const headerUserActionBillingSx = {
  bgcolor: '#e5474c',
  width: 50,
  minWidth: 50,
}

export const headerUserActionLogoutSx = {
  bgcolor: '#797d95',
  width: 50,
  minWidth: 50,
}

export const headerUserWhiteIconSx = {
  color: 'white',
}

export const pageTitleDefaultIconSx = {
  fontSize: 30,
  color: 'inherit',
}

export const publicNavRowMarginSx = (isHome) => ({
  mt: isHome ? 5 : 0,
})

export const publicNavFlexSpacerSx = {
  flex: 1,
}

export const publicNavWhiteIconSx = {
  color: 'white',
}

export const publicNavMenuBtnSx = {
  mr: '10px',
}

export const colorModeToggleIconWrapSx = {
  display: 'inline-flex',
}

export const headerMenuSectionTitlePrimarySx = {
  color: '#4191ff',
}

export const sidebarSharedSectionLabelSx = {
  color: 'white',
  opacity: 0.6,
  cursor: 'default',
  justifyContent: 'flex-start',
  pl: 0,
  display: 'flex',
  alignItems: 'center',
  my: 1,
}

export const sidebarSharedSectionIconSx = {
  ml: '10px',
  mr: '10px',
}

export function sidebarChevronIndicatorSx(open) {
  return {
    transition: 'transform 0.2s',
    display: 'inline-block',
    transform: open ? 'rotate(90deg)' : 'rotate(0deg)',
  }
}

export const sidebarNestedNavLinkSx = {
  pl: '32px',
  display: 'flex',
  alignItems: 'center',
}

export const sidebarNestedExternalLinkSx = {
  ...sidebarNestedNavLinkSx,
  color: 'white',
}

export const notificationDrawerBodySx = {
  p: '15px',
}

export const notificationDrawerTitleSx = {
  ml: '5px',
}

export const notificationDrawerSectionSpacerSx = {
  mt: '10px',
}

export const notificationActionTextSx = {
  whiteSpace: 'normal',
}

export const notificationActionLinkSx = {
  color: '#368CFF',
  textDecoration: 'underline',
}

export const notificationListItemCenterSx = {
  textAlign: 'center',
}

export const notificationListItemFlexSpacerSx = {
  flex: 1,
}

export const notificationAvatarIconSx = {
  display: 'flex',
  alignSelf: 'center',
}

export const sidebarNavigationSx = {
  '& [data-hf="sidebar-menu-box"]': {
    borderRadius: RADIUS_SM,
    margin: '1rem',
    padding: '1rem',
    '& ul': { margin: 0 },
    '& ul li a': {
      fontSize: '0.875rem',
      borderRadius: RADIUS_SM,
      padding: '0 0.75rem',
      '& [data-hf="sidebar-item-label"] .badge': { right: '0.75rem' },
    },
    '& ul li ul li a': {
      padding: '0 0 0 1.5rem',
      '&::after': { display: 'none' },
    },
    '& [data-hf="sidebar-header-label"]': { padding: '0 0.75rem 0.75rem' },
  },
  '& [data-hf="sidebar-header-label"]': {
    textTransform: 'uppercase',
    fontSize: '0.833rem',
    padding: '1rem 1.5rem 0.75rem',
    height: 'initial',
    transition: TRANSITION,
    whiteSpace: 'nowrap',
    margin: 0,
    position: 'relative',
    '&::after': {
      position: 'absolute',
      height: '1px',
      left: 0,
      bottom: 0,
      width: '100%',
      opacity: 0,
      visibility: 'hidden',
      transition: TRANSITION,
      content: '""',
    },
  },
  '& ul': {
    paddingLeft: 0,
    listStyle: 'none',
    display: 'flex',
    flexDirection: 'column',
    margin: 0,
  },
  '& ul li': {
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    padding: '1px 0',
  },
  '& ul li a': {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    alignContent: 'center',
    height: NAVLINK_H,
    padding: '0 1.5rem',
    fontSize: '1.06rem',
    whiteSpace: 'nowrap',
    fontWeight: 'bold',
    '&.nav-link-simple [data-hf="sidebar-icon-indicator-right"], &[data-hf="nav-link-simple"] [data-hf="sidebar-icon-indicator-right"]': {
      transform: 'rotate(0deg) !important',
    },
    '&.active [data-hf="sidebar-icon-indicator"], &[aria-current="page"] [data-hf="sidebar-icon-indicator"], &[data-nav-active] [data-hf="sidebar-icon-indicator"]': {
      transform: 'rotate(-90deg)',
    },
    '&.active.nav-link-simple [data-hf="sidebar-icon-indicator-right"], &[aria-current="page"][data-hf="nav-link-simple"] [data-hf="sidebar-icon-indicator-right"], &[data-nav-active][data-hf="nav-link-simple"] [data-hf="sidebar-icon-indicator-right"]': {
      transform: 'rotate(0deg) !important',
    },
  },
  '& [data-hf="sidebar-item-label"]': {
    height: 22,
    lineHeight: '22px',
    opacity: 1,
    visibility: 'visible',
    overflow: 'visible',
    '& .badge': {
      position: 'absolute',
      right: '1.5rem',
      height: 22,
      lineHeight: '24px',
      top: '50%',
      marginTop: -11,
      transition: TRANSITION,
      opacity: 1,
      visibility: 'visible',
    },
  },
  '& [data-hf="sidebar-icon-indicator"]': {
    transform: 'rotate(0deg)',
    transition: TRANSITION,
    opacity: 0.4,
    marginLeft: 'auto',
    width: 18,
    height: 18,
    '& > *': {
      maxHeight: '100%',
      width: '100%',
      display: 'block',
    },
  },
  '& [data-hf="sidebar-icon"]': {
    margin: '0 0.5556rem 0 0',
    width: 28,
    minWidth: 28,
    height: 28,
    flexShrink: 0,
    display: 'flex',
    fontSize: '1.495rem',
    transition: TRANSITION,
    alignItems: 'center',
    alignContent: 'center',
    '& > *': {
      maxHeight: '90%',
      width: '90%',
      display: 'block',
      transition: TRANSITION,
    },
    '& .MuiSvgIcon-root': {
      fontSize: '1.35rem',
      width: '1.35rem',
      height: '1.35rem',
    },
  },
  '& .MuiDivider-root': {
    borderColor: '#46506c',
    backgroundColor: 'transparent',
  },
  '& ul li ul': {
    overflow: 'hidden',
    padding: '0.75rem 0 1rem',
    transition: TRANSITION,
  },
  '& ul li ul li': {
    padding: '1px 0.5rem 1px 1.5rem',
  },
  '& ul li ul li a': {
    borderRadius: RADIUS,
    fontSize: '1rem',
    position: 'relative',
    margin: 0,
    fontWeight: 'normal',
    padding: '0 1.25rem 0 2.3077rem',
    height: NAVLINK_INNER_H,
    whiteSpace: 'nowrap',
    transition:
      'transform 0.2s, height 0.3s, opacity 0.3s, margin 0.3s, color 0.3s, background-color 0.3s',
    display: 'flex',
    alignItems: 'center',
    '& .MuiTypography-root': { color: 'inherit' },
    '& [data-hf="sidebar-item-label"]': { transition: 'none' },
    '&::after': {
      content: '""',
      left: '12px',
      transition: TRANSITION,
      width: '8px',
      height: '8px',
      top: '50%',
      position: 'absolute',
      margin: '-4px 0 0 -4px',
      opacity: 0,
      backgroundColor: 'currentColor',
      borderRadius: '100%',
    },
    '&:hover::after, &.active::after, &[aria-current="page"]::after, &[data-nav-active]::after': {
      opacity: 1,
    },
    '&:has(> [data-hf="sidebar-icon"])::after, &:has(> .svg-inline--fa)::after, &:has(> .MuiSvgIcon-root)::after': {
      display: 'none',
    },
  },
}

export const sidebarUserboxSx = {
  borderRadius: RADIUS_SM,
  padding: SPACER,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  margin: '1rem 1.5rem 0.6rem',
  position: 'relative',
  flexDirection: 'column',
  textAlign: 'center',
  transition: CHROME_TRANSITION,
  '& [data-hf="btn-userbox"]': { whiteSpace: 'nowrap', boxShadow: 'none' },
  '& [data-hf="card-tr-actions"]': {
    position: 'absolute',
    right: '0.5rem',
    top: '0.3333rem',
    transition: CHROME_TRANSITION,
    visibility: 'visible',
    opacity: 1,
  },
  '& .avatar-icon-wrapper': {
    margin: 0,
    transition: CHROME_TRANSITION,
    '& .badge-circle': { width: 10, height: 10 },
  },
}

export const sidebarMenuCollapsedSx = {
  paddingLeft: 0,
  listStyle: 'none',
  padding: '0.3333rem 0',
  '& li': {
    textAlign: 'center',
    padding: '4px 0',
    '& a': {
      borderRadius: RADIUS_SM,
      display: 'flex',
      padding: 0,
      width: 50,
      height: 44,
      margin: '0 auto',
      transition: TRANSITION,
      alignItems: 'center',
      justifyContent: 'center',
    },
  },
}

export function footerBarSx({
  shadow = false,
  opacityBg = false,
  fixed = false,
  collapsed = false,
} = {}) {
  return {
    height: FOOTER_HEIGHT,
    display: 'flex',
    alignItems: 'center',
    background: opacityBg ? 'rgba(255, 255, 255, 0.9)' : WHITE,
    width: '100%',
    padding: `0 ${LAYOUT}`,
    transition: CHROME_TRANSITION,
    fontSize: '0.875rem',
    justifyContent: 'space-between',
    boxShadow: shadow ? BOX_SHADOW_LAYOUT_TOP : 'none',
    '& .MuiList-root': {
      justifyContent: 'center',
      '& .MuiButtonBase-root': {
        textAlign: 'center',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        whiteSpace: 'nowrap',
      },
      '& .MuiListItem-root': { width: 'auto' },
    },
    [DOWN_LG]: {
      display: 'block',
      textAlign: 'center',
      height: 'auto',
      padding: '0.3333rem 0.5rem 0.5rem',
    },
    '&:hover': { background: `${WHITE} !important` },
    '& [data-hf="footer-second"] span:first-of-type': {
      [DOWN_LG]: {
        display: 'block',
        marginBottom: '0.5rem',
      },
    },
    [DARK_MODE_SEL]: {
      background: opacityBg
        ? 'rgba(26, 26, 26, 0.95) !important'
        : '#1a1a1a !important',
      color: 'rgba(255, 255, 255, 0.65) !important',
      borderTop: '1px solid rgba(255, 255, 255, 0.12)',
      '&:hover': { background: '#1e1e1e !important' },
      '& a, & span, & .MuiSvgIcon-root, & .MuiIconButton-root, & .MuiLink-root':
        {
          color: 'rgba(255, 255, 255, 0.7) !important',
        },
      '& a:hover, & .MuiIconButton-root:hover': {
        color: 'rgba(255, 255, 255, 0.95) !important',
      },
    },
    ...(fixed
      ? {
          position: 'fixed',
          bottom: 0,
          zIndex: 22,
          left: collapsed ? SIDEBAR_WIDTH_COLLAPSED : SIDEBAR_WIDTH,
          right: 0,
          width: 'auto',
          [UP_ML]: collapsed
            ? { left: SIDEBAR_WIDTH_COLLAPSED }
            : { left: SIDEBAR_WIDTH },
        }
      : {}),
  }
}

export function pageTitleRootSx({ shadow = false, compact = false } = {}) {
  return {
    margin: compact ? '-3rem 0 3rem' : `-${LAYOUT} -${LAYOUT} ${LAYOUT}`,
    padding: compact ? LAYOUT : '1rem 3rem',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    background: 'rgba(255, 255, 255, 0.8)',
    transition: TRANSITION,
    boxShadow: shadow ? HEADER_SHADOW : 'none',
    [DOWN_SM]: {
      margin: '-1rem -1rem 1rem',
      padding: '1rem',
      flexDirection: 'column',
    },
    [DARK_MODE_SEL]: {
      ...chromeHeaderDarkSx,
      background: '#1e1e1e !important',
      borderBottom: '1px solid rgba(255, 255, 255, 0.12)',
      boxShadow: 'none',
    },
  }
}

export const pageTitleFirstSx = {
  flexGrow: 1,
  display: 'flex',
  alignItems: 'center',
  [DOWN_SM]: { flexDirection: 'column', marginBottom: '1.5rem' },
}

export const pageTitleHeadingSx = {
  paddingRight: SPACER,
  [DOWN_SM]: { paddingRight: 0, textAlign: 'center' },
  '& h1': { fontSize: '1.545rem', fontWeight: 700, margin: 0 },
  [DARK_MODE_SEL]: {
    '& h1': { color: 'rgba(255, 255, 255, 0.95)' },
  },
}

export const pageTitleIconboxSx = {
  background: WHITE,
  borderRadius: RADIUS_SM,
  boxShadow: BOX_SHADOW_SM_LIGHT,
  display: 'flex',
  margin: '0 1.5rem 0 0',
  justifyContent: 'center',
  alignItems: 'center',
  [DOWN_SM]: { display: 'none' },
  [DARK_MODE_SEL]: {
    background: '#2a2a2a !important',
    boxShadow: 'none',
    color: 'rgba(255, 255, 255, 0.87)',
  },
}

export const pageTitleLogoWrapSx = {
  display: 'flex',
  alignItems: 'center',
  margin: '0 1.5rem 0 0',
  [DOWN_SM]: {
    margin: '0 0 1.5rem',
    justifyContent: 'center',
    width: '100%',
  },
}

export const pageTitleLogoSx = {
  display: 'block',
  height: 56,
  width: 'auto',
  maxWidth: 'min(280px, 70vw)',
  objectFit: 'contain',
}

export const pageTitleDescriptionSx = {
  margin: '0.5rem 0 0',
  fontSize: '1.1rem',
  opacity: 0.6,
  fontWeight: 'normal',
  [DARK_MODE_SEL]: {
    color: 'rgba(255, 255, 255, 0.65)',
    opacity: 1,
  },
}

export function headerDrawerPaperSx() {
  return {
    transition: DRAWER_TRANSITION,
    boxShadow: BOX_SHADOW_LAYOUT_LEFT,
    maxWidth: 450,
    width: '100%',
    background: WHITE,
    zIndex: 1210,
    overflow: 'visible',
    overflowX: 'visible',
    overflowY: 'visible',
    top: `${HEADER_HEIGHT}px`,
    height: `calc(100% - ${HEADER_HEIGHT}px)`,
    boxSizing: 'border-box',
    border: 'none',
    backgroundImage: 'none',
    '& [data-hf="close-drawer-btn"]': {
      transition: TRANSITION,
      position: 'absolute',
      left: -52,
      width: 40,
      height: 40,
      margin: 0,
      top: 19,
      padding: 0,
      border: `2px solid ${WHITE}`,
      background: '#253152',
      color: WHITE,
      boxShadow: BOX_SHADOW_SM,
      zIndex: 1215,
      opacity: 1,
      visibility: 'visible',
      borderRadius: '100%',
      [DOWN_SM]: { marginLeft: 30 },
    },
  }
}

export function notificationDrawerPaperSx() {
  return {
    transition: DRAWER_TRANSITION,
    boxShadow: BOX_SHADOW_LAYOUT_LEFT,
    maxWidth: 450,
    width: '100%',
    background: WHITE,
    zIndex: 1210,
    overflow: 'visible',
    overflowX: 'visible',
    overflowY: 'visible',
    top: `${HEADER_HEIGHT}px`,
    height: `calc(100% - ${HEADER_HEIGHT}px)`,
    boxSizing: 'border-box',
    border: 'none',
    backgroundImage: 'none',
    '& [data-hf="close-notification-drawer-btn"]': {
      transition: TRANSITION,
      position: 'absolute',
      left: -19,
      width: 38,
      height: 38,
      margin: 0,
      top: 19,
      padding: 0,
      border: 0,
      background: WHITE,
      color: PRIMARY,
      boxShadow: BOX_SHADOW_SM,
      zIndex: 1215,
      opacity: 1,
      visibility: 'visible',
      borderRadius: '100%',
      [DOWN_SM]: { marginLeft: 30 },
    },
  }
}

export const drawerBackdropSx = {
  background:
    'linear-gradient(90deg, rgba(7, 9, 25, 0.1) 25%, rgba(7, 9, 25, 0.6) 100%)',
}

export const drawerModalRootSx = {
  overflow: 'visible',
  // AppBar is 1201; default drawer modal is 1200 and tucks under the header.
  zIndex: 1300,
}

export function headerSearchWrapperSx({ open = false } = {}) {
  return {
    borderRight: open
      ? 'transparent solid 1px'
      : 'rgba(0, 0, 0, 0.1) solid 1px',
    padding: '0.2857rem 1rem 0.2857rem 0',
    margin: '0 2rem 0 0',
    cursor: 'pointer',
    '& [data-hf="search-icon"]': {
      position: 'absolute',
      left: 12,
      top: 7,
      zIndex: 10,
    },
    '& [data-hf="search-field"] .MuiFormControl-root': {
      padding: 0,
      width: open ? '100%' : 42,
      opacity: open ? 1 : 0,
      visibility: open ? 'visible' : 'hidden',
      transition: TRANSITION,
      cursor: 'pointer',
      ...(open ? { '& .MuiInputBase-input': { paddingLeft: 42 } } : {}),
    },
    '& [data-hf="search-field"] .MuiInputBase-root': {
      height: 36,
      '& input': { cursor: open ? 'text' : 'pointer' },
    },
    [DOWN_MD]: {
      borderLeft: 'rgba(0, 0, 0, 0.1) solid 1px',
      borderRight: 'transparent solid 1px',
      padding: '0.2857rem 0.5rem',
      margin: `0 ${SPACER}`,
    },
  }
}

export function headerMenuSx({ hidden = false } = {}) {
  return {
    position: 'relative',
    transition: TRANSITION,
    transform: hidden ? 'scale(0)' : 'scale(1)',
    opacity: hidden ? 0 : 1,
    visibility: hidden ? 'hidden' : 'visible',
    transitionDelay: hidden ? 0 : '0.2s',
    [DOWN_MD]: { display: 'none' },
  }
}

export const headerDotsPaperSx = { overflow: 'hidden', minHeight: 493 }
export const headerDotsPreviewSx = { height: 260, width: 400 }

export function innerContentLayoutSx({ fixed = false } = {}) {
  return {
    display: 'flex',
    flexDirection: 'column',
    [UP_ML]: { flexDirection: 'row' },
    ...(fixed
      ? {
          bottom: 0,
          left: 0,
          right: 0,
          top: 0,
          position: 'absolute',
        }
      : {}),
  }
}

export const innerContentMainSx = {
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  minWidth: 0,
  [DOWN_MD]: { height: `calc(100vh - ${HEADER_HEIGHT + 80}px)` },
  '@media (min-width: 1100px)': { order: 2 },
}

const SIDEBAR_SIZES = {
  sm: 280,
  lg: 380,
  xl: 380,
  xxl: 520,
}

export function innerContentSidebarSx({
  size = 'lg',
  open = false,
  posRight = false,
} = {}) {
  const width = SIDEBAR_SIZES[size] || 280
  return {
    width: size === 'xxl' || size === 'xl' ? 380 : width,
    display: 'flex',
    transition: CHROME_TRANSITION,
    flexDirection: 'column',
    position: 'relative',
    zIndex: 900,
    boxShadow: SIDEBAR_SHADOW_LIGHT,
    ...(size === 'xl'
      ? { width: 380, [DOWN_LG]: { width: 280 } }
      : {}),
    ...(size === 'xxl'
      ? { width: 520, [DOWN_LG]: { width: 380 } }
      : {}),
    ...(size === 'lg' ? { width: 380 } : {}),
    ...(size === 'sm' ? { width: 280 } : {}),
    [DOWN_MD]: {
      left: posRight ? 'auto' : open ? 0 : -380,
      right: posRight ? (open ? 0 : -380) : 'auto',
      position: 'fixed',
      zIndex: 1175,
      top: 0,
      height: '100vh',
      width: 280,
      paddingTop: HEADER_HEIGHT,
      boxShadow: open
        ? posRight
          ? BOX_SHADOW_LAYOUT_LEFT
          : SIDEBAR_SHADOW
        : 'none',
    },
    ...(posRight ? { position: 'relative' } : {}),
    // Only the right/workflow panel is order 3; left property nav stays first.
    ...(posRight
      ? { '@media (min-width: 1100px)': { order: 3 } }
      : {}),
    [DARK_MODE_SEL]: {
      backgroundColor: '#1e1e1e !important',
      color: 'rgba(255, 255, 255, 0.87)',
      '& .MuiTypography-root, & .MuiIconButton-root, & .MuiSvgIcon-root': {
        color: 'rgba(255, 255, 255, 0.87)',
      },
      '& .MuiCheckbox-root': { color: 'rgba(255, 255, 255, 0.55)' },
      '& .MuiCheckbox-root.Mui-checked': { color: '#1bc943' },
    },
  }
}

export { SIDEBAR_FOOTER_HEIGHT, FOOTER_HEIGHT, UP_ML, DOWN_ML }
